"use client";

import { cn } from "@/lib/utils";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";
import React from "react";

const typographyVariants = cva("", {
  variants: {
    variant: {
      hero: "text-6xl leading-120 font-bold",
      h1: "text-5xl leading-120 font-bold",
      h2: "text-4xl leading-120 font-semibold",
      h3: "text-3xl leading-120 font-semibold",
      subheading1: "text-2xl leading-130 font-semibold",
      subheading2: "text-xl leading-130 font-semibold",
      subheading3: "text-sm leading-130 font-bold",
      body1: "text-lg leading-140 font-medium",
      body2: "text-base leading-140 font-medium",
      body2_5: "text-base leading-140 font-normal",
      body3: "text-sm leading-140 font-medium",
      body4: "text-sm leading-140 font-normal",
      detail1: "text-xs leading-120 font-normal",
      detail2: "text-2xs leading-140 font-normal",
    },
    tone: {
      white: "text-white",
      lightsilver: "text-gray-300",
      silver: "text-gray-350",
      subtle: "text-gray-400",
      description: "text-gray-500",
      light: "text-gray-525",
      lightmedium: "text-gray-550",
      lightnormal: "text-gray-600",
      normal: "text-gray-700",
      darknormal: "text-gray-725",
      lightstrong: "text-gray-750",
      medium: "text-gray-800",
      strong: "text-gray-900",
      darkstrong: "text-gray-950",
      inherit: "text-inherit",
    },
    align: {
      left: "text-left",
      center: "text-center",
      right: "text-right",
      justify: "text-justify",
    },
  },
  defaultVariants: {
    variant: "body2_5",
    tone: "normal",
    align: "left",
  },
});

const defaultTagByVariant = {
  hero: "h1",
  h1: "h1",
  h2: "h2",
  h3: "h3",
  subheading1: "h4",
  subheading2: "h5",
  subheading3: "h6",
  body1: "p",
  body2: "p",
  body2_5: "p",
  body3: "p",
  body4: "p",
  detail1: "span",
  detail2: "span",
} as const;

export type VariantKeys = NonNullable<
  VariantProps<typeof typographyVariants>["variant"]
>;

type ElementType = React.ElementType;
type OwnProps = VariantProps<typeof typographyVariants> & {
  asChild?: boolean;
  className?: string;
  children?: React.ReactNode;
};

type PolymorphicProps<T extends ElementType> = OwnProps & {
  as?: T;
} & Omit<React.ComponentPropsWithoutRef<T>, keyof OwnProps | "as">;

export function Typography<T extends ElementType = "p">({
  as,
  asChild,
  variant = "body2_5",
  tone = "normal",
  align = "left",
  className,
  children,
  ...rest
}: PolymorphicProps<T>) {
  const fallbackTag = variant
    ? (defaultTagByVariant[variant as VariantKeys] as ElementType)
    : ("p" as ElementType);

  const Comp = (asChild ? Slot : (as ?? fallbackTag)) as ElementType;

  return (
    <Comp
      {...rest}
      className={cn(typographyVariants({ variant, tone, align }), className)}
    >
      {children}
    </Comp>
  );
}
